import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getCoachDashboard, fetchFollowedTeams, fetchTeamRoster, loadMoreTeamRoster, setSelectedTeam, clearTeamRoster, unfollowPlayer, unfollowTeam } from '../../features/coach/coachProfileSlice';
import { WelcomeBanner } from '../../components/coach/dashboard/WelcomeBanner';
import { PipelineCard } from '../../components/coach/dashboard/PipelineCard';
import { FollowPlayersCard } from '../../components/coach/dashboard/FollowPlayersCard';
import { FollowedProfiles } from '../../components/coach/dashboard/FollowedProfiles';
import { StatsSection } from '../../components/coach/dashboard/StatsSection';
import { WelcomeCoachModal } from '../coach/WelcomeCoachModal';
import { CoachSidebar } from '../../components/coach/dashboard/CoachSidebar';
import { currentSeasonYear } from '../../utils/season';

export const CoachDashboard = () => {
  const { activeView } = useOutletContext();
  const dispatch = useDispatch();
  const {
    profile,
    followedPlayers,
    topPlayers,
    dashboardLoading,
    followedTeams,
    followedTeamsLoading,
    teamRoster,
    teamRosterLoading,
    selectedTeam,
    teamRosterPagination
  } = useSelector((state) => state.coachProfile);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [activeProfileTab, setActiveProfileTab] = useState('players'); // 'players' or 'teams'

  useEffect(() => {
    // Fetch coach dashboard data on mount
    dispatch(getCoachDashboard({ page: 1, limit: 20 }));
    // Fetch followed teams
    dispatch(fetchFollowedTeams({ page: 1, limit: 100 }));
  }, [dispatch]);

  useEffect(() => {
    // Check if welcome modal has been shown before
    const hasSeenWelcomeModal = localStorage.getItem('coachWelcomeModalSeen');

    if (!hasSeenWelcomeModal) {
      // Show modal after 2 seconds only if not seen before
      const timer = setTimeout(() => {
        setShowWelcomeModal(true);
      }, 2000);

      // Cleanup timer on unmount
      return () => clearTimeout(timer);
    }
  }, []);

  // Fetch team roster when a team is selected
  useEffect(() => {
    if (selectedTeam?._id) {
      dispatch(fetchTeamRoster({ teamId: selectedTeam._id, page: 1, limit: 50, seasonYear: currentSeasonYear() }));
    }
  }, [selectedTeam, dispatch]);

  const handleLoadMoreRoster = () => {
    if (selectedTeam?._id && teamRosterPagination.hasMore && !teamRosterLoading) {
      const nextPage = teamRosterPagination.currentPage + 1;
      dispatch(loadMoreTeamRoster({
        teamId: selectedTeam._id,
        page: nextPage,
        limit: 50,
        seasonYear: currentSeasonYear()
      }));
    }
  };

  const handleTabChange = (tab) => {
    setActiveProfileTab(tab);
    if (tab === 'players') {
      dispatch(clearTeamRoster());
    }
  };

  const handleTeamSelect = (team) => {
    dispatch(setSelectedTeam(team));
  };

  const handleCloseWelcomeModal = () => {
    setShowWelcomeModal(false);
    // Do not save to localStorage here - only save when checkbox is checked
  };

  const handleUnfollowPlayer = async (playerId) => {
    try {
      await dispatch(unfollowPlayer(playerId)).unwrap();
      // Refetch dashboard data to update followed players list
      dispatch(getCoachDashboard({ page: 1, limit: 20 }));
    } catch (error) {
      console.error('Failed to unfollow player:', error);
    }
  };

  const handleUnfollowTeam = async (teamId) => {
    try {
      await dispatch(unfollowTeam(teamId)).unwrap();
      // Refetch followed teams list
      dispatch(fetchFollowedTeams({ page: 1, limit: 100 }));
      // If the unfollowed team was selected, clear the roster
      if (selectedTeam?._id === teamId) {
        dispatch(clearTeamRoster());
      }
    } catch (error) {
      console.error('Failed to unfollow team:', error);
    }
  };

  return (
    <>
      {/* Top Section - Welcome & Cards - Full Width */}
      {/* <div className="border border-jp-line rounded-2xl grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <WelcomeBanner />
        <PipelineCard />
        <FollowPlayersCard />
      </div> */}

      {/* Grid with Sidebar and Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left Sidebar */}
        <div className="lg:col-span-1">
          <CoachSidebar activeView={activeView} />
        </div>

        {/* Right Content Area */}
        <div className="lg:col-span-3 bg-jp-card border border-jp-line rounded-3xl shadow-sm">
          <FollowedProfiles
            followedPlayers={followedPlayers}
            followedTeams={followedTeams}
            topPlayers={topPlayers}
            loading={dashboardLoading}
            teamsLoading={followedTeamsLoading}
            onTeamSelect={handleTeamSelect}
            onTabChange={handleTabChange}
            selectedTeam={selectedTeam}
            onUnfollowPlayer={handleUnfollowPlayer}
            onUnfollowTeam={handleUnfollowTeam}
          />
          <StatsSection
            hasFollowedPlayers={followedPlayers && followedPlayers.length > 0}
            followedPlayers={followedPlayers}
            activeProfileTab={activeProfileTab}
            teamRoster={teamRoster}
            teamRosterLoading={teamRosterLoading}
            selectedTeam={selectedTeam}
            teamRosterPagination={teamRosterPagination}
            onLoadMoreRoster={handleLoadMoreRoster}
          />
        </div>
      </div>

      {/* Welcome Coach Modal */}
      <WelcomeCoachModal
        isOpen={showWelcomeModal}
        onClose={handleCloseWelcomeModal}
        coachName={profile?.firstName || 'COACH'}
      />
    </>
  );
};

export default CoachDashboard;
